import React, { useState, useEffect } from 'react';
import { Card, Col, Row, ListGroup, Button, Badge, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Routes } from '#Routes';
import { useChangeNameMutation, useFetchUserPreferencesQuery, useUpdateMarketingPreferencesMutation } from '#store';

const AccountDetails = () => {
  const { t } = useTranslation();
  const patient = useSelector((state) => state.patient);
  const isEmailVerified = useSelector((state) => state.auth.isEmailVerified);

  const [name, setName] = useState(patient.name);
  const [isEditing, setIsEditing] = useState(false);
  const [marketingUnsubscribed, setMarketingUnsubscribed] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  const [changeName] = useChangeNameMutation();
  const { data: userPreferences, isLoading: isLoadingPreferences } = useFetchUserPreferencesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [updateMarketingPreferences] = useUpdateMarketingPreferencesMutation();

  useEffect(() => {
    if (userPreferences) {
      setMarketingUnsubscribed(userPreferences.marketing_emails_unsubscribed || false);
    }
  }, [userPreferences]);

  const handleNameChange = (e) => {
    setName(e.target.value);
  };

  const saveName = async () => {
    try {
      await changeName({ name }).unwrap();
      setIsEditing(false);
      toast.error(t('auth.name-toast-success'));
    } catch (error) {
      console.error('Failed to change name:', error);
      toast.error(t('auth.name-toast-error'));
      setIsEditing(false);
    }
  };

  const handleToggleMarketingPreferences = async (value) => {
    try {
      setSavingPreferences(true);
      const result = await updateMarketingPreferences({
        marketing_emails_unsubscribed: value,
      }).unwrap();
      setMarketingUnsubscribed(result.marketing_emails_unsubscribed);
      toast.success(
        value
          ? t('emailPreferences.unsubscribedSuccess')
          : t('emailPreferences.subscribedSuccess')
      );
    } catch (error) {
      console.error('Failed to update marketing preferences:', error);
      toast.error(t('emailPreferences.updateError'));
    } finally {
      setSavingPreferences(false);
    }
  };

  return (
    <Card as='form' border='0' className='shadow p-0 p-md-4 h-100'>
      <Card.Header className='border-bottom p-3'>
        <h5 className='mb-0'>{t('account.account-details')}</h5>
      </Card.Header>
      <Card.Body className='px-0 py-0'>
        <ListGroup>
          <ListGroup.Item className='py-3 border-bottom'>
            <Row className='align-items-center'>
              <Col>
                <h6 className='mb-1'>{t('account.email')}</h6>
              </Col>
              <Col xs='auto'>
                <b>{patient.email}</b>
                {!isEmailVerified && (
                  <Badge bg='warning' className='ms-1'>
                    {t('account.emailUnverified')}
                  </Badge>
                )}
              </Col>
              {!isEmailVerified && (
                <Col xs='auto'>
                  <Button as={Link} variant='logo-green' to={Routes.ResendEmailVerification.path}>
                    {t('auth.resendEmailVerification')}
                  </Button>
                </Col>
              )}
            </Row>
          </ListGroup.Item>
          <ListGroup.Item className='py-3 border-bottom'>
            <Row className='align-items-center'>
              <Col>
                <h6 className='mb-1'>{t('account.password')}</h6>
              </Col>
              <Col xs='auto'>
                <Button as={Link} variant='logo-green' to={Routes.ChangePassword.path}>
                  {t('auth.changePassword')}
                </Button>
              </Col>
            </Row>
          </ListGroup.Item>
          <ListGroup.Item className='py-3 border-bottom'>
            <Row className='align-items-center'>
              <Col>
                <h6 className='mb-1'>{t('auth.name')}</h6>
                {isEditing ? (
                  <Form.Control type='text' value={name} onChange={handleNameChange} />
                ) : (
                  <p>{name}</p>
                )}
              </Col>
              <Col xs='auto'>
                {isEditing ? (
                  <Button variant='logo-green' onClick={saveName}>
                    {t('auth.name-save')}
                  </Button>
                ) : (
                  <Button variant='logo-green' onClick={() => setIsEditing(true)}>
                    {t('auth.name-edit')}
                  </Button>
                )}
              </Col>
            </Row>
          </ListGroup.Item>
          <ListGroup.Item className='py-3'>
            <Row className='align-items-center'>
              <Col>
                <h6 className='mb-1'>{t('emailPreferences.title')}</h6>
                {isLoadingPreferences ? (
                  <p className='text-muted small'>{t('emailPreferences.loading')}</p>
                ) : (
                  <Form.Check
                    type='checkbox'
                    id='marketing-emails-account'
                    checked={!marketingUnsubscribed}
                    onChange={(e) => handleToggleMarketingPreferences(!e.target.checked)}
                    disabled={savingPreferences}
                    label={t('emailPreferences.wantToReceiveEmails')}
                    className='mt-2'
                  />
                )}
                <p className='text-muted small mt-2 mb-0'>
                  {marketingUnsubscribed
                    ? t('emailPreferences.unsubscribedHelpText')
                    : t('emailPreferences.subscribedHelpText')}
                </p>
              </Col>
            </Row>
          </ListGroup.Item>
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

export default React.memo(AccountDetails);
